import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { backend, API_URL } from "../../../config";
import { initialStateSelectedItem } from "../scripts/constants";

const useModalActions = ({ products, setProducts }) => {
  const productsStore = useSelector((state) => state.products);

  const [customModal, setCustomModal] = useState(false);
  const [defaultUPCModal, setDefaultUPCModal] = useState(false);
  const [orderlistInfo, setOrderlistInfo] = useState([]);
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [productInfo, setProductInfo] = useState(initialStateSelectedItem);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [action, setAction] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleCategoryChange = (e) => {
    const { name, value } = e.target;
    const [olId] = name.split("-");
    setSelectedCategory((prevState) =>
      prevState === null ? { [olId]: value } : { ...prevState, [olId]: value },
    );
  };

  const toggleCustomModal = useCallback(
    () => setCustomModal((prev) => !prev),
    [],
  );

  const toggleDefaultUPCModal = useCallback(
    () => setDefaultUPCModal((prev) => !prev),
    [],
  );

  const dismissDefaultUPCModal = (update = false) => {
    if (update) defaultUPCUpdateCheckProducts();
    setLoadingFlag(false);
    setFinishedFlag(false);
    setErrorInfo({ error: false, message: "" });
    setProductInfo(initialStateSelectedItem);
    setAction(null);
    setSelectedCategory(null);
    setOrderlistInfo([]);
    toggleDefaultUPCModal();
  };

  const dismissCustomModal = () => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    setErrorInfo({ error: false, message: "" });
    setProductInfo(initialStateSelectedItem);
    setAction(null);
    setSelectedCategory(null);
    setOrderlistInfo([]);
    toggleCustomModal();
  };

  const fetchNDeployDefaultUPCModal = useCallback(
    (productObj, itemcodeRelated, multiSearch = false) => {
      setLoadingFlag(true);
      setProductInfo(productObj);
      toggleDefaultUPCModal();

      const productsToMigrate = itemcodeRelated.filter(
        (item) => item.upc !== productObj.upc,
      );

      let orderlistsRelated = [];
      productsToMigrate.forEach(
        (item) =>
          (orderlistsRelated = [...orderlistsRelated, ...item.order_lists]),
      );

      let order_listsInfo = [];

      Promise.all(
        orderlistsRelated.map(async (item) => {
          await backend
            .get(`${API_URL}orderlists/${item}`)
            .then(({ data }) => {
              let crOldUPC = undefined;

              const categoryOld = data.categories.find((cItem) =>
                cItem.rows.some((crItem) => {
                  const crProduct = productsStore.find(
                    (psItem) => psItem.upc === crItem.product_id,
                  );

                  if (!crProduct) return false;

                  crOldUPC = crProduct.upc;

                  if (multiSearch) {
                    return productsToMigrate.some(
                      (migrateItem) => migrateItem.upc === crProduct.upc,
                    );
                  }

                  return (
                    productObj.item_code.item_code ===
                      crProduct.item_code.item_code &&
                    productObj.upc !== crProduct.upc
                  );
                }),
              );

              if (!categoryOld) return;

              const dataMap = {
                ...data,
                oldCategory: categoryOld.category_name,
                categoryOldUPC: crOldUPC,
              };

              order_listsInfo = [...order_listsInfo, dataMap];
            })
            .catch((error) => {
              setErrorInfo({
                error: true,
                message: error.response
                  ? JSON.stringify(error.response.data)
                  : error.message || "Network error, please try again",
              });
              setLoadingFlag(false);
            });
        }),
      ).then(() => {
        setOrderlistInfo(order_listsInfo);

        const preSelected = {};
        order_listsInfo.forEach((oiItem) => {
          const oldCategoryIndex = oiItem.categories.findIndex(
            (cat) => cat.category_name === oiItem.oldCategory,
          );
          if (oldCategoryIndex !== -1) {
            preSelected[oiItem.id] = oldCategoryIndex;
          }
        });

        setSelectedCategory(preSelected);
        setLoadingFlag(false);
      });
    },
    [productsStore, toggleDefaultUPCModal],
  );

  const fetchNDeployCustomModal = useCallback(
    async (orderlistId, productObj, checked) => {
      setLoadingFlag(true);
      setProductInfo(productObj);
      setAction(checked);
      toggleCustomModal();

      await backend
        .get(`${API_URL}orderlists/${orderlistId}`)
        .then(({ data }) => {
          setOrderlistInfo([data]);
          setLoadingFlag(false);
        })
        .catch((error) => {
          setErrorInfo({
            error: true,
            message: JSON.stringify(error.response.data),
          });
        });
    },
    [toggleCustomModal],
  );

  const updateCheckProducts = () => {
    setProducts((prevState) =>
      prevState.map((productItem) => {
        if (productInfo.upc !== productItem.upc) return productItem;

        const oiObj = orderlistInfo[0];

        const newOrderLists = action
          ? [...productItem.order_lists, oiObj.id]
          : productItem.order_lists.filter((olId) => olId !== oiObj.id);

        return {
          ...productItem,
          order_lists: newOrderLists,
          modified: true,
        };
      }),
    );
  };

  const defaultUPCUpdateCheckProducts = () => {
    let oiIdsRemove = {};
    const oiIdsAdd = orderlistInfo.map((item) => item.id);

    orderlistInfo.forEach((item) => {
      oiIdsRemove[item.categoryOldUPC] = oiIdsRemove[item.categoryOldUPC]
        ? [...oiIdsRemove[item.categoryOldUPC], item.id]
        : [item.id];
    });

    setProducts((prevState) =>
      prevState.map((productItem) => {
        if (productInfo.upc === productItem.upc) {
          return {
            ...productItem,
            order_lists: [
              ...new Set([...productItem.order_lists, ...oiIdsAdd]),
            ],
            modified: true,
          };
        }

        if (Object.keys(oiIdsRemove).includes("" + productItem.upc)) {
          return {
            ...productItem,
            order_lists: productItem.order_lists.filter(
              (olId) => !oiIdsRemove[productItem.upc].includes(olId),
            ),
            modified: true,
          };
        }

        return productItem;
      }),
    );
  };

  const categoryRequest = async () => {
    const oiObj = orderlistInfo[0];

    if (action) {
      const categoryObj = oiObj.categories[selectedCategory[oiObj.id]];
      const payloadObj = {
        upc: productInfo.upc,
        category_name: categoryObj.category_name,
      };

      await backend
        .post(`${API_URL}orderlists/${oiObj.id}/addproduct/`, payloadObj)
        .then(() => updateCheckProducts())
        .catch((error) => {
          setErrorInfo({
            error: true,
            message: JSON.stringify(error.response.data),
          });
        });
    } else {
      const categoriesArray = oiObj.categories.map((catItem) => {
        const productIndex = catItem.rows.findIndex(
          (rItem) => rItem.product_id === productInfo.upc,
        );

        if (productIndex === -1) return catItem;

        const catRows = [...catItem.rows];
        catRows.splice(productIndex, 1);

        return { ...catItem, rows: catRows };
      });

      const orderListObj = {
        id: oiObj.id,
        company_location: oiObj.company_location,
        categories: categoriesArray,
        visible: oiObj.visible,
      };

      await backend
        .patch(`${API_URL}orderlists/${orderListObj.id}`, orderListObj)
        .then(() => updateCheckProducts())
        .catch((error) => {
          setErrorInfo({
            error: true,
            message: JSON.stringify(error.response.data),
          });
        });
    }
  };

  const defaultUPCCategoryRequest = async (oiObj, add, defaultUPC = false) => {
    const categoryObj = oiObj.categories[selectedCategory[oiObj.id]];

    let payloadObj = {
      upc: productInfo.upc,
      category_name: categoryObj.category_name,
    };

    let actionEndpoint = add ? "addproduct" : "removeproduct";

    if (!add) {
      payloadObj = {
        upc: oiObj.categoryOldUPC,
        category_name: oiObj.oldCategory,
      };
    }

    await backend
      .post(`${API_URL}orderlists/${oiObj.id}/${actionEndpoint}/`, payloadObj)
      .then(() => {
        if (!defaultUPC) updateCheckProducts();
        if (defaultUPC && !add) defaultUPCCategoryRequest(oiObj, true, true);
      })
      .catch((error) => {
        setErrorInfo({
          error: true,
          message: JSON.stringify(error.response.data),
        });
      });
  };

  const sendDefaultUPCRequest = async () => {
    setLoadingFlag(true);
    await Promise.all(
      orderlistInfo.map((item) => {
        defaultUPCCategoryRequest(item, false, true);
        return item;
      }),
    ).then(() => {
      setLoadingFlag(false);
      setFinishedFlag(true);
    });
  };

  const sendRequest = async () => {
    setLoadingFlag(true);
    Promise.all([categoryRequest()]).then(() => {
      setLoadingFlag(false);
      setFinishedFlag(true);
    });
  };

  return {
    customModal,
    defaultUPCModal,
    orderlistInfo,
    loadingFlag,
    finishedFlag,
    productInfo,
    errorInfo,
    action,
    selectedCategory,
    handleCategoryChange,
    toggleCustomModal,
    toggleDefaultUPCModal,
    dismissCustomModal,
    dismissDefaultUPCModal,
    fetchNDeployCustomModal,
    fetchNDeployDefaultUPCModal,
    sendRequest,
    sendDefaultUPCRequest,
  };
};

export default useModalActions;